/* eslint-disable @typescript-eslint/no-explicit-any */
import { File } from 'better-xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { ICacNotViewedStudent } from '../models/statistics.model';
import { getCommonCodeLabel } from './store';

const COLOR_HEADER_BG = 'BB0665';
const COLOR_WHITE = 'FFFFFFFF';

/** 데이터 행 높이 — exportTable.js와 동일 */
const ROW_HEIGHT_CM = 0.8;

/** exportTable.js 기본 `cellWidths = [20, 20, 20, 20]` 와 동일 */
const cellWidths = [20, 20, 20, 20];
const resolveColWidth = (index: number) =>
  cellWidths[index] ?? cellWidths[cellWidths.length - 1];

const setHeaderStyle = (cell: any) => {
  cell.style.fill.patternType = 'solid';
  cell.style.fill.fgColor = COLOR_HEADER_BG;
  cell.style.fill.bgColor = COLOR_HEADER_BG;
  cell.style.font.color = COLOR_WHITE;
  cell.style.font.bold = true;
  cell.style.align.h = 'center';
  cell.style.align.v = 'center';
};

const setDataStyle = (
  cell: any,
  horizontal: 'center' | 'left' = 'center',
) => {
  cell.style.align.h = horizontal;
  cell.style.align.v = 'center';
};

const formatDate = (d?: string) => {
  if (!d) return '';
  if (d.length === 8) return moment(d, 'YYYYMMDD').format('YYYY-MM-DD');
  return d;
};

const buildConditionText = (
  excludeUpperLevels: boolean,
  excludeMiddleLevels: boolean,
) => {
  const parts: string[] = [];
  if (excludeUpperLevels) parts.push('Deca·Hendeca 제외');
  if (excludeMiddleLevels) parts.push('중등 제외');
  return parts.join(', ');
};

export interface ICacNotViewedExportArgs {
  srtDate: string;
  endDate: string;
  baseDate?: string;
  excludeUpperLevels: boolean;
  excludeMiddleLevels: boolean;
  students: ICacNotViewedStudent[];
}

/** CAC 미조회 학생 — 상단 헤더(학생 기준일/조회기간/조회 조건) + 학생 목록 */
const exportCacNotViewed = (args: ICacNotViewedExportArgs) => {
  const {
    srtDate,
    endDate,
    baseDate,
    excludeUpperLevels,
    excludeMiddleLevels,
    students,
  } = args;

  const file = new File();
  const sheet = file.addSheet('CAC 미조회 학생');

  const addHeaderRow = (
    label: string,
    value: string,
    valueHorizontal: 'center' | 'left' = 'center',
  ) => {
    const row = sheet.addRow();
    row.setHeightCM(ROW_HEIGHT_CM);
    const c1 = row.addCell();
    c1.value = label;
    setHeaderStyle(c1);
    const c2 = row.addCell();
    c2.value = value;
    setDataStyle(c2, valueHorizontal);
  };
  addHeaderRow('학생 기준일', formatDate(baseDate || endDate));
  addHeaderRow(
    '조회기간',
    `${formatDate(srtDate)} ~ ${formatDate(endDate)}`,
    'left',
  );
  addHeaderRow(
    '조회 조건',
    buildConditionText(excludeUpperLevels, excludeMiddleLevels),
  );

  sheet.addRow().setHeightCM(ROW_HEIGHT_CM);

  const titles = [
    '캠퍼스',
    '교육 단계',
    '학생 이름',
    '아이디',
    '학생코드',
    '단계',
    '수강반',
    '담임',
  ];
  const header = sheet.addRow();
  header.setHeightCM(ROW_HEIGHT_CM);
  titles.forEach((title) => {
    const c = header.addCell();
    c.value = title;
    setHeaderStyle(c);
  });

  students.forEach((s) => {
    const r = sheet.addRow();
    r.setHeightCM(ROW_HEIGHT_CM);
    const values = [
      s.campusKorName,
      getCommonCodeLabel('PR0006', s.eduStepCode, 'ko'),
      s.studentName,
      s.webId,
      s.studentCode,
      s.courseName,
      s.className,
      s.homeroomName,
    ];
    values.forEach((v) => {
      const c = r.addCell();
      c.value = v;
      setDataStyle(c);
    });
  });

  for (let i = 0; i < titles.length; i += 1) {
    sheet.col(i).width = resolveColWidth(i);
  }

  const fileName = `CAC 미조회 학생_${moment().format('YYYYMMDD')}`;
  file.saveAs('blob').then((content: Blob) => {
    saveAs(content, `${fileName}.xlsx`);
  });
};

export default exportCacNotViewed;
